/**
 * SavedNotesList - List of saved notes with edit/delete actions
 */

import { useState } from 'react';

// Icons
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const NoteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

export interface SavedNote {
  id: string;
  title: string;
  content: string;
  sourceType: 'chat_response' | 'summary' | 'manual';
  createdAt: number;
  updatedAt: number;
}

interface SavedNotesListProps {
  notes: SavedNote[];
  isLoading: boolean;
  onUpdate: (noteId: string, title: string, content: string) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
}

export function SavedNotesList({
  notes,
  isLoading,
  onUpdate,
  onDelete,
}: SavedNotesListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Start editing a note
  const handleStartEdit = (note: SavedNote) => {
    setEditingId(note.id);
    setEditTitle(note.title);
    setEditContent(note.content);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditContent('');
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingId) return;

    setIsSaving(true);
    try {
      await onUpdate(editingId, editTitle, editContent);
      setEditingId(null);
      setEditTitle('');
      setEditContent('');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete note with confirmation
  const handleDelete = async (noteId: string) => {
    if (window.confirm('Delete this note?')) {
      await onDelete(noteId);
    }
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="notes-loading">
        <div className="loading-skeleton" />
        <div className="loading-skeleton" />
        <style>{`
          .notes-loading {
            padding: 0;
          }
          .loading-skeleton {
            height: 60px;
            background: linear-gradient(90deg, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 8px;
            margin-bottom: 8px;
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="notes-empty">
        <p>No saved notes yet</p>
        <p className="empty-hint">
          Save chat responses or create manual notes.
        </p>
        <style>{`
          .notes-empty {
            padding: 40px 0;
            text-align: center;
          }
          .notes-empty p {
            margin: 0;
            font-size: 13px;
            color: rgba(148, 163, 184, 0.6);
          }
          .notes-empty .empty-hint {
            margin-top: 4px;
            font-size: 12px;
            color: rgba(100, 116, 139, 0.5);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="notes-list">
      {notes.map((note) => (
        <div key={note.id} className="note-item">
          {editingId === note.id ? (
            // Edit mode
            <div className="note-edit-form">
              <input
                type="text"
                className="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
              />
              <textarea
                className="edit-content"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Content"
                rows={4}
              />
              <div className="edit-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="save-btn"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            // View mode
            <>
              <div
                className="note-header"
                onClick={() => setExpandedId(expandedId === note.id ? null : note.id)}
              >
                <span className="note-icon">
                  <NoteIcon />
                </span>
                <div className="note-info">
                  <span className="note-title">{note.title}</span>
                  <span className="note-meta">
                    {formatDate(note.updatedAt)}
                    {note.sourceType === 'summary' && ' • Summary'}
                    {note.sourceType === 'chat_response' && ' • From chat'}
                  </span>
                </div>
                <div className="note-actions">
                  <button
                    type="button"
                    className="action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartEdit(note);
                    }}
                    title="Edit"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    className="action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(note.id);
                    }}
                    title="Delete"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>

              {/* Expanded content */}
              {expandedId === note.id && (
                <div className="note-content">
                  {note.content}
                </div>
              )}
            </>
          )}
        </div>
      ))}

      <style>{`
        /* === DARK THEME NOTES LIST === */
        .notes-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .note-item {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          overflow: hidden;
        }

        .note-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .note-header:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .note-icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 6px;
          color: rgba(129, 140, 248, 0.8);
          flex-shrink: 0;
        }

        .note-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .note-title {
          font-size: 13px;
          font-weight: 500;
          color: rgba(226, 232, 240, 0.9);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .note-meta {
          font-size: 11px;
          color: rgba(100, 116, 139, 0.6);
        }

        .note-actions {
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.15s;
        }

        .note-header:hover .note-actions {
          opacity: 1;
        }

        .action-btn {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 4px;
          color: rgba(148, 163, 184, 0.6);
          cursor: pointer;
          transition: all 0.15s;
        }

        .action-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .action-btn.delete:hover {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }

        .note-content {
          padding: 0 12px 12px 50px;
          font-size: 13px;
          line-height: 1.6;
          color: rgba(148, 163, 184, 0.8);
          white-space: pre-wrap;
        }

        .note-edit-form {
          padding: 12px;
        }

        .edit-title {
          width: 100%;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #fff;
          outline: none;
          margin-bottom: 8px;
        }

        .edit-title::placeholder {
          color: rgba(100, 116, 139, 0.5);
        }

        .edit-title:focus {
          border-color: rgba(99, 102, 241, 0.4);
        }

        .edit-content {
          width: 100%;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          font-size: 13px;
          color: rgba(226, 232, 240, 0.9);
          outline: none;
          resize: vertical;
          font-family: inherit;
          margin-bottom: 8px;
        }

        .edit-content::placeholder {
          color: rgba(100, 116, 139, 0.5);
        }

        .edit-content:focus {
          border-color: rgba(99, 102, 241, 0.4);
        }

        .edit-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .cancel-btn {
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          font-size: 12px;
          color: rgba(148, 163, 184, 0.8);
          cursor: pointer;
          transition: all 0.15s;
        }

        .cancel-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        .save-btn {
          padding: 6px 12px;
          background: #6366f1;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #fff;
          cursor: pointer;
          transition: all 0.15s;
        }

        .save-btn:hover:not(:disabled) {
          background: #818cf8;
        }

        .save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
